// ./src/components/features/Connectors/ConfigEditors/GcpPubSubConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Text,
  Anchor,
  Alert,
  Code,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { GcpPubSubConnectorConfig } from "@/types";

export interface GcpPubSubConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: GcpPubSubConnectorConfig = {
  projectId: "",
  credentials: "",
  topicName: "",
  orderingKey: "",
};

/**
 * Parse and validate GCP Pub/Sub connector configuration
 */
const parseGcpPubSubConfig = (jsonString: string): GcpPubSubConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const GcpPubSubConnectorConfigEditor: React.FC<
  GcpPubSubConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<GcpPubSubConnectorConfig>(() =>
    parseGcpPubSubConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseGcpPubSubConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: GcpPubSubConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleProjectIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, projectId: e.target.value });
  };

  const handleCredentialsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    updateConfig({ ...config, credentials: e.target.value });
  };

  const handleTopicNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, topicName: e.target.value });
  };

  const handleOrderingKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, orderingKey: e.target.value });
  };

  // Validate credentials JSON
  const credentialsError = (() => {
    if (!config.credentials) return undefined;
    try {
      JSON.parse(config.credentials);
      return undefined;
    } catch {
      return "Must be valid JSON";
    }
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create a Pub/Sub topic in{" "}
          <Anchor
            href="https://console.cloud.google.com/cloudpubsub/topic/list"
            target="_blank"
            rel="noopener noreferrer"
          >
            GCP Console
          </Anchor>
          {" → Pub/Sub → Topics. Ensure the service account has "}
          <Code>roles/pubsub.publisher</Code>
          {" permission."}
        </Text>
      </Alert>

      {/* Project ID Field */}
      <TextInput
        label="Project ID"
        placeholder="my-project-123456"
        description="GCP Project ID"
        value={config.projectId}
        onChange={handleProjectIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Credentials Field */}
      <Textarea
        label="Service Account Credentials (JSON)"
        placeholder='{"type": "service_account", "project_id": "...", ...}'
        description="Paste the entire contents of the service account JSON key file"
        value={config.credentials}
        onChange={handleCredentialsChange}
        disabled={disabled}
        required
        size="md"
        minRows={4}
        maxRows={8}
        autosize
        error={credentialsError}
        styles={{
          input: {
            fontFamily: "monospace",
            fontSize: "12px",
          },
        }}
      />

      {/* Topic Name Field */}
      <TextInput
        label="Topic Name"
        placeholder="form-submissions"
        description="Name of the Pub/Sub topic (must already exist)"
        value={config.topicName}
        onChange={handleTopicNameChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Ordering Key Field */}
      <TextInput
        label="Ordering Key"
        placeholder="{{formId}}"
        description="Optional key for message ordering (messages with the same key are delivered in order)"
        value={config.orderingKey || ""}
        onChange={handleOrderingKeyChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for ordering key:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* Message Ordering Note */}
      <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
        <Text size="sm">
          <strong>Note:</strong> To use message ordering, your Pub/Sub topic
          must have message ordering enabled. Messages with the same ordering
          key are delivered to subscribers in the order they were published.
        </Text>
      </Alert>
    </Stack>
  );
};
